import { memo, useMemo } from 'react';
import { Checkbox, Skeleton, Stack } from '@mui/material';
import { SortingRule } from 'react-table';
import Table, { ColumnOption, renderDate } from 'domains/core/components/Table';
import { AccessCentreUser } from 'domains/accessCentre/types';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import { SortDirection } from 'models/enums';
import UsersTableRoleSelectionCell from './UsersTableRoleSelectionCell';

/** Matches GET /users `sort` query param field names. */
const defaultSortingRules: SortingRule<object>[] = [{ id: 'last_modified_timestamp', desc: true }];

const noopOnSort = (_columnId: string, _direction: SortDirection) => {};

export type BulkSelectCallbacks = {
    isUserSelected: (id: string) => boolean;
    onToggleUser: (user: AccessCentreUser) => void;
};

type Props = {
    users: AccessCentreUser[];
    isLoading?: boolean;
    showChangeRole?: boolean;
    /** When set with `showChangeRole`, renders row checkboxes for bulk selection */
    bulkSelect?: BulkSelectCallbacks;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
};

const UsersTable = ({ users, isLoading, showChangeRole = false, bulkSelect, onSort = noopOnSort }: Props) => {
    const data: object[] = useMemo(
        () =>
            users.map((u) => ({
                ...u,
                lastModifiedSort: new Date(u.last_modified_timestamp ?? 0),
            })),
        [users]
    );

    const selectColumn: ColumnOption[] = useMemo(() => {
        if (!showChangeRole || !bulkSelect) {
            return [];
        }
        return [
            {
                id: 'select',
                Header: '',
                accessor: 'id',
                disableSortBy: true,
                align: 'center',
                sx: { width: 52, maxWidth: 52 },
                Cell: (cellProps: any) => {
                    const raw = cellProps.row.original as AccessCentreUser & { lastModifiedSort?: Date };
                    const { lastModifiedSort: _lm, ...user } = raw;
                    return (
                        <Checkbox
                            checked={bulkSelect.isUserSelected(user.id)}
                            onChange={() => bulkSelect.onToggleUser(user as AccessCentreUser)}
                            inputProps={{
                                'aria-label': `Select ${user.name}`,
                            }}
                            data-testid={`access-center-users-row-select-${user.id}`}
                            size="small"
                            sx={{ p: 0.5 }}
                        />
                    );
                },
            },
        ];
    }, [showChangeRole, bulkSelect]);

    const columns: ColumnOption[] = useMemo(
        () => [
            ...selectColumn,
            {
                id: 'name',
                Header: 'User Name',
                accessor: 'name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'email',
                Header: 'Email',
                accessor: 'email',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'role_name',
                Header: 'Role',
                accessor: 'role_name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => (
                    <UsersTableRoleSelectionCell
                        user={row.row.original as AccessCentreUser}
                        showChangeRole={showChangeRole}
                    />
                ),
            },
            {
                id: 'last_modified_timestamp',
                Header: 'Date Updated',
                accessor: 'lastModifiedSort',
                sortType: 'datetime',
                Cell: renderDate,
            },
        ],
        [selectColumn, showChangeRole]
    );

    if (isLoading) {
        return (
            <Stack
                spacing={1}
                sx={{ pt: 2 }}
                data-testid="access-center-users-table-loading"
                role="progressbar"
                aria-label="Loading users"
            >
                {[1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={48} />
                ))}
            </Stack>
        );
    }

    return (
        <Stack data-testid="access-center-users-table" role="table" aria-label="Users table">
            <Table
                columns={columns}
                data={data}
                defaultSortingRules={defaultSortingRules}
                manualSortBy
                onSort={onSort}
                stickyHeader
            />
        </Stack>
    );
};

export default memo(UsersTable);
